import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DoseScheduleAnchorReason,
  DoseScheduleAnchorRowReason,
  DoseScheduleView,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchDoseSchedule,
  setDoseScheduleAnchor,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1194 (Ö-388 c, Ö-389 b; Emelie 2026-10-08) — the patient's weekly dose
// schedule on the treatment overview, and the doctor's two changes to it:
//   "Ändra ordinarie dosdag"      — a new dose day from a date on;
//   "Ny startdag efter uppehåll"  — offered when a pause has ended, the
//                                   medically confirmed first dose after it.
// The server computes everything the patient sees (Home's dose task, "Din
// behandling"'s next dose, the weekly and monthly check-in); this only sets
// the anchor and shows the result. Doctor only, outside the admin's
// read-only view: the coach may not move the dose day. The staff-facing
// strings are proposed copy.

// TES-1522 — a corrected first dose re-anchors the schedule too (Ö-789 b).
// Its label uses the doctor's button words (Ö-807); her own correction has
// no doctor's name and says so in the dose log's words.
const REASON_LABEL: Record<DoseScheduleAnchorRowReason, string> = {
  dose_day_moved: "Ny ordinarie dosdag",
  restart_after_pause: "Ny startdag efter uppehåll",
  first_dose_corrected: "Rättat datum för första dosen, ny ordinarie dosdag",
};

// The server's refusals, in the words the doctor reads.
const ERROR_TEXT: Record<string, string> = {
  dose_schedule_unavailable:
    "Patienten har inget veckovis dosschema att ändra.",
  dose_schedule_paused:
    "Behandlingen är pausad. Dosschemat kan ändras när den har återupptagits.",
  dose_schedule_restart_required:
    "Bekräfta först en ny startdag efter uppehållet.",
  dose_schedule_no_pause:
    "Det finns inget avslutat uppehåll att sätta en ny startdag för.",
  dose_day_unchanged:
    "Datumet ligger redan på den ordinarie dosdagen. Välj en annan dag.",
  dose_schedule_date_too_early: "Datumet är för tidigt. Välj ett senare datum.",
  dose_schedule_conflict:
    "Dosschemat ändrades samtidigt av någon annan. Ladda om och försök igen.",
};

// A calendar date (YYYY-MM-DD) read as that day, whatever the browser zone.
function asDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

function formatDay(iso: string): string {
  return asDay(iso).toLocaleDateString("sv-SE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function weekdayOf(iso: string): string {
  return asDay(iso).toLocaleDateString("sv-SE", {
    weekday: "long",
    timeZone: "UTC",
  });
}

type Mode = DoseScheduleAnchorReason | null;

export function DoseSchedulePanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const isDoctor = claims?.role === "doctor" && !readOnly;
  const [view, setView] = useState<DoseScheduleView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [mode, setMode] = useState<Mode>(null);
  const [anchorOn, setAnchorOn] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const load = useCallback(async () => {
    try {
      setView(await fetchDoseSchedule(patientId));
      setLoadError(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      setLoadError(true);
    }
  }, [patientId]);

  useEffect(() => {
    if (isDoctor) void load();
  }, [isDoctor, load]);

  if (!isDoctor) return null;

  if (loadError) {
    return (
      <p className="text-xs text-textSecondary">
        Dosschemat kunde inte läsas. Ladda om sidan.
      </p>
    );
  }
  if (!view) return null;

  if (!view.scheduled) {
    return (
      <p className="text-sm text-textSecondary">
        Inget veckovis dosschema. Det kräver en ordination en gång i veckan och
        att patienten har registrerat sin första dos.
      </p>
    );
  }

  function open(next: DoseScheduleAnchorReason) {
    setMode(next);
    setSaved(false);
    setError(null);
    setNote("");
    setAnchorOn(
      (next === "dose_day_moved" ? view?.moveFrom : view?.restartFrom) ?? "",
    );
  }

  async function save() {
    if (!mode || !anchorOn) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      setView(
        await setDoseScheduleAnchor(patientId, {
          reason: mode,
          anchorOn,
          ...(trimmed ? { note: trimmed } : {}),
        }),
      );
      setMode(null);
      setSaved(true);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      const code = e instanceof Error ? e.message : "";
      setError(ERROR_TEXT[code] ?? "Ändringen kunde inte sparas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  const min = mode === "dose_day_moved" ? view.moveFrom : view.restartFrom;

  return (
    <div className="flex flex-col gap-sm text-sm">
      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs">
        {view.regularFrom ? (
          <>
            <dt className="text-textSecondary">Ordinarie dosdag</dt>
            <dd>
              {`${weekdayOf(view.regularFrom)} (från ${formatDay(view.regularFrom)})`}
            </dd>
          </>
        ) : null}
        <dt className="text-textSecondary">Nästa planerade dos</dt>
        <dd>{view.nextDoseOn ? formatDay(view.nextDoseOn) : "–"}</dd>
        {view.firstDoseOn ? (
          <>
            <dt className="text-textSecondary">Första dos</dt>
            <dd>{formatDay(view.firstDoseOn)}</dd>
          </>
        ) : null}
      </dl>

      {view.paused ? (
        <p className="text-xs text-textSecondary">
          Behandlingen är pausad. Dosschemat kan ändras när den har
          återupptagits.
        </p>
      ) : null}
      {view.awaitingRestartSince ? (
        <p className="text-xs text-textPrimary" role="status">
          Behandlingen har återupptagits efter ett uppehåll. Ingen dos planeras
          förrän du har bekräftat en ny startdag.
        </p>
      ) : null}
      {saved ? (
        <p className="text-xs text-textSecondary" role="status">
          Dosschemat är uppdaterat. Patienten ser nästa dos i appen.
        </p>
      ) : null}

      {mode && min ? (
        <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
          <label className="flex flex-col gap-xs">
            <span>
              {mode === "dose_day_moved"
                ? "Ny dosdag från och med"
                : "Första dos efter uppehållet"}
            </span>
            <input
              type="date"
              value={anchorOn}
              min={min}
              onChange={(e) => setAnchorOn(e.target.value)}
              disabled={busy}
              className="self-start rounded-md border border-surfaceMuted px-sm py-xs"
            />
          </label>
          <p className="text-xs text-textSecondary">
            {mode === "dose_day_moved"
              ? "Veckodagen för datumet blir den nya ordinarie dosdagen. Doser före datumet ligger kvar som planerat, och veckoincheckningen följer den nya behandlingsveckan."
              : "Dosschemat räknas från det här datumet. Ange ett medicinskt bekräftat datum, till exempel dagen för första dosen efter uppehållet."}
          </p>
          {anchorOn ? (
            <p className="text-xs text-textSecondary">
              {`Ny ordinarie dosdag: ${weekdayOf(anchorOn)}.`}
            </p>
          ) : null}
          <label className="flex flex-col gap-xs">
            <span>Anteckning (valfri)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              disabled={busy}
              className="rounded-md border border-surfaceMuted px-sm py-xs"
            />
          </label>
          <div className="flex flex-wrap items-center gap-sm">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !anchorOn || anchorOn < min}
              className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              {mode === "dose_day_moved"
                ? "Spara ny dosdag"
                : "Bekräfta ny startdag"}
            </button>
            <button
              type="button"
              onClick={() => setMode(null)}
              disabled={busy}
              className="text-sm text-textSecondary underline"
            >
              Avbryt
            </button>
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : (
        <div className="flex flex-wrap gap-sm">
          {view.restartFrom ? (
            <button
              type="button"
              onClick={() => open("restart_after_pause")}
              className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand"
            >
              Ny startdag efter uppehåll
            </button>
          ) : null}
          {view.moveFrom ? (
            <button
              type="button"
              onClick={() => open("dose_day_moved")}
              className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand"
            >
              Ändra ordinarie dosdag
            </button>
          ) : null}
        </div>
      )}

      {view.anchors.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-medium text-textSecondary">
            Tidigare ändringar
          </h3>
          <ul className="flex flex-col gap-xs text-xs">
            {view.anchors.map((a) => (
              <li key={a.id}>
                {`${REASON_LABEL[a.reason]} från ${formatDay(a.anchorOn)}`}
                {a.setByName
                  ? ` · ${a.setByName}`
                  : a.reason === "first_dose_corrected"
                    ? " · Rättad av patienten"
                    : ""}
                {` · ${new Date(a.createdAt).toLocaleDateString("sv-SE")}`}
                {a.note ? (
                  <span className="block text-textSecondary">{a.note}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
